import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Button } from '@/components/ui/daisy';
import Fieldset from '@/components/ui/daisy/fieldset';
import { useFillInTheBlankOptions } from '@/data/hook/question/answers/useFillInTheBlankOptions';
import { PlusIcon } from '@heroicons/react/24/outline';
import { FillInTheBlankFormFields } from './FillInTheBlankForm';
import FillInTheBlankOptionItem from './FillInTheBlankOptionItem';
import usePedWorkspace from '@/data/hook/ped-content/usePedWorkspace';
import LimitedEditionWrapper from '@/components/shared/LimitedEditionWrapper';

type FillInTheBlankOptionsSectionProps = {
  blankCount: number;
};

export default function FillInTheBlankOptionsSection({
  blankCount,
}: FillInTheBlankOptionsSectionProps) {
  const { t: tCommon } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.questionsTab.createQuestionModal.answersCommon',
  });
  const { t } = useTranslation('translation', {
    keyPrefix:
      'courseLineForm.questionsTab.createQuestionModal.fillInTheBlankForm',
  });

  const { watch } = useFormContext<FillInTheBlankFormFields>();
  const liveOptions = watch('options');

  const {
    fields,
    error,
    handleAdd,
    handleUpdateText,
    handleUpdateMedia,
    handleUpdateValidPositions,
    handleMarkIncorrect,
    handleDelete,
    isNewItem,
  } = useFillInTheBlankOptions({ blankCount });

  const { isLimitedEdition, isLoading } = usePedWorkspace();

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h3 className="text-base font-semibold">{t('responseFieldsTitle')}</h3>
        <ConditionalRenderer condition={!!error?.root?.message} fallback={null}>
          <Fieldset.Label className="text-error">
            {error?.root?.message}
          </Fieldset.Label>
        </ConditionalRenderer>
      </div>

      <LimitedEditionWrapper
        isLimitedEdition={isLimitedEdition}
        className="w-fit"
        placement="right"
      >
        <Button
          type="button"
          variant="soft"
          color="primary"
          className="self-start"
          onClick={handleAdd}
          disabled={isLimitedEdition}
        >
          <PlusIcon className="size-3.5" />
          {tCommon('addOption')}
        </Button>
      </LimitedEditionWrapper>

      <div className="flex flex-wrap gap-6">
        {fields.map((field, index) => (
          <FillInTheBlankOptionItem
            key={field.fieldId}
            item={liveOptions?.[index] ?? field}
            totalPositions={blankCount}
            initialEditing={isNewItem({ index })}
            onUpdateText={text => handleUpdateText(index, text)}
            onUpdateMedia={(file, altText) =>
              handleUpdateMedia(index, file, altText)
            }
            onUpdateValidPositions={positions =>
              handleUpdateValidPositions(index, positions)
            }
            onMarkIncorrect={() => handleMarkIncorrect(index)}
            onDelete={() => handleDelete(index)}
            isLimitedEdition={isLimitedEdition}
            isLoading={isLoading}
          />
        ))}
      </div>
    </div>
  );
}
